/**
 * A proteção de quem mostra a cena 3D dos dados (a camada da mesa e a caixinha de teste das
 * Configurações). Sem WebGL (aceleração por hardware desligada, placa de vídeo bloqueada pelo
 * navegador), o three.js não consegue desenhar e joga um erro. Se ninguém pegar esse erro, o React
 * desmonta o RPG Desk inteiro e a tela fica vazia.
 */
import { Component, type ReactNode } from 'react';
import { no3dReason, type No3DReason } from './no3dHelp.js';

/** Há WebGL 2? Sem ele, o motivo (no3dHelp.ts) e a mensagem do navegador, se ele deu uma. */
export type WebGLStatus = { ok: true } | { ok: false; reason: No3DReason; message: string | null };

let webgl: WebGLStatus | undefined;

/**
 * O navegador desenha em WebGL 2, que é o que o three.js pede? A pergunta é feita uma vez por página,
 * num canvas de teste que é solto logo em seguida. Ao recusar, o navegador conta o motivo no evento
 * `webglcontextcreationerror`, que chega antes de o `getContext` voltar.
 */
export function webglStatus(): WebGLStatus {
  if (webgl === undefined) {
    let message: string | null = null;
    try {
      const canvas = document.createElement('canvas');
      canvas.addEventListener('webglcontextcreationerror', (event) => {
        message = (event as WebGLContextEvent).statusMessage || null;
      });
      const gl = canvas.getContext('webgl2');
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      webgl = gl ? { ok: true } : { ok: false, reason: no3dReason(message), message };
    } catch {
      webgl = { ok: false, reason: 'unknown', message };
    }
  }
  return webgl;
}

/** Sem WebGL, a cena nem monta: os dados somem sem erro. */
export function hasWebGL(): boolean {
  return webglStatus().ok;
}

/** Se a cena quebrar mesmo assim (falha ao carregar, por exemplo), quem a usa continua: só os dados somem. */
export class SceneBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch() {
    this.props.onError();
  }
  override render() {
    return this.state.failed ? null : this.props.children;
  }
}
